/* eslint no-eval: 0 */
import React, { useState, useRef, useEffect } from "react";
import { v4 as uuid } from "uuid";
import * as _ from "lodash";
import Lottie from "lottie-react";
import dot from "dot-object";

import ChatMessageButton from "../components/ChatMessageButton";
import ChatMessageCustom from "../components/ChatMessageCustom";
import ChatMessageText from "../components/ChatMessageText";
import ChatMessageStatus from "../components/ChatMessageStatus";

import * as animationData from "../assets/lottie/confetti.json";
import { ConversationInterface, ConversationStep } from "../interfaces";
import ChatMesageInputTypeahead from "../components/ChatMesageInputTypeahead";
import ChatMessageVideo from "../components/ChatMessageVideo";
import ChatMessageInput from "../components/ChatMessageInput";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { chatActions } from "../store/chat/slice";

const Conversation: React.FC<ConversationInterface> = ({ steps, theme, switchFlow }) => {
    const dispatch = useAppDispatch();
    const messages = useAppSelector((state) => state.chat.messages);
    const chatHasBeenEscalated = useAppSelector((state) => state.chat.chatHasBeenEscalated);
    const bodyRef = useRef(null);
    const defaultValues = {
        mortgage_type: theme?.version === "protection" ? "Protection Review" : "",
        ...(theme?.version === "booking"
            ? {
                  talk_with_advisor: "Yes",
              }
            : {}),
        theme,
        //mortgage_type: "First Home",
        ...(theme?.version === "short" && {
            mortgage_type: "First Home",
        }),
    };
    const [playAnimation, setPlayAnimation] = useState(true);
    const [currentCustomTrigger, setCurrentCustomTrigger] = useState(null);
    const [values, setValues] = useState<any>(defaultValues);

    const scrollToBottom = () => {
        if (bodyRef.current) {
            const element = bodyRef.current! as HTMLDivElement;
            element.scrollTop = element.scrollHeight;
        }
    };

    const handleCustomTrigger = async (currentStep: ConversationStep, value: string | number) => {
        if (currentStep?.id === "connect_mortgage" && value === "Yes" && currentCustomTrigger) {
            const {
                theme: {
                    name,
                    email,
                    botAvatar,
                    primaryColor,
                    secondaryColor,
                    logo,
                    expertPhoneNumber,
                    switchToMortgageText,
                    criteria,
                },
            } = currentCustomTrigger!;
            //switch to mortgage_type
            const newFlowSteps = switchFlow("mortgage", {
                ...theme,
                name,
                email,
                logo,
                customization: {
                    botAvatar,
                    primaryColor,
                    secondaryColor,
                    expertPhoneNumber,
                    switchToMortgageText,
                    criteria,
                },
            });
            setCurrentCustomTrigger(null);

            await new Promise((resolve) => setTimeout(resolve, 1000));

            return new Promise((resolve) => {
                const step = newFlowSteps.find(
                    (step: ConversationStep) => step.id === "mortgage_type"
                );
                if (step) {
                    step.message =
                        "First, let me check the mortgage market for you. What type of mortgage do you need?";
                }
                resolve(step);
            });
        }
    };

    const handleSwitchFlow = (currentStep: ConversationStep) => {
        const customTrigger = theme?.customization?.triggers?.find(
            (trigger) => trigger?.triggerOn === currentStep?.id
        );
        //trigger here
        switch (customTrigger?.type) {
            case "switch-flow":
                const { triggerOn, triggerToCheckId, triggerToCheckValue, switchTo } =
                    customTrigger;

                if (
                    currentStep?.id === triggerOn &&
                    ((values as any)[triggerToCheckId] || "") === triggerToCheckValue
                ) {
                    switch (switchTo) {
                        case "property-to-mortgage":
                            setCurrentCustomTrigger(customTrigger);
                            return steps.find((step) => step.id === "connect_mortgage");
                        default:
                            break;
                    }
                }
                break;
            default:
                break;
        }
    };

    const setDataValues = (data: any) => {
        setValues({
            ...values,
            ...data,
        });
    };

    const getValidatorErrorMessage = async (currentStep: any, value: any) => {
        for (const validator of currentStep?.validator) {
            const condition = validator.condition;
            const error = validator.error;
            const formedCondition = condition.replace("{values.amount}", value);
            const evalCondition = eval(formedCondition);
            if (!evalCondition) {
                return error;
            }
        }
    };

    const checkInvalidInput = async (currentStep: ConversationStep, value: any) => {
        if (currentStep?.validator) {
            if (Array.isArray(currentStep?.validator)) {
                return getValidatorErrorMessage(currentStep, value);
            } else {
                const result = await currentStep?.validator!(value, values);

                if (typeof result === "string") {
                    return true;
                }
            }
        }
        return false;
    };

    const handleInputSubmit = async (
        currentStep: ConversationStep,
        userText: any,
        trigger: string
    ) => {
        if (userText) {
            if (currentStep?.validator && (await checkInvalidInput(currentStep, userText))) {
                //invalid should output error message and trigger question again
                const validatorValue = await checkInvalidInput(currentStep, userText);
                const errorId = uuid();
                let userMessage = `${
                    currentStep?.formatter ? currentStep?.formatter(userText) : userText
                }` as string;

                if (Array.isArray(userMessage)) {
                    userMessage = userText[userText.length - 1];
                }
                const userStep: ConversationStep = {
                    id: uuid(),
                    type: "user",
                    escalated: currentStep?.escalated || chatHasBeenEscalated,
                    user: {
                        firstName: values?.applicant1_firstName,
                        lastName: values?.applicant1_lastName,
                        email: values?.applicant1_email,
                    },
                    message: userMessage,
                    trigger: errorId,
                    theme,
                };
                const validatorMsg = Array.isArray(currentStep.validator)
                    ? validatorValue
                    : await currentStep?.validator!(userText, values);
                const errorStep: ConversationStep = {
                    id: errorId,
                    theme,
                    type: "bot",
                    message: validatorMsg,
                    trigger: currentStep?.id,
                };

                steps.push(errorStep);

                //setCurrentStep(userStep);
                //setRenderedSteps([...renderedSteps, userStep]);
                dispatch(
                    chatActions.sendToCMS({
                        message: userStep,
                    })
                );
            } else {
                let local_tempValue;
                if (currentStep?.input?.currency) {
                    const _tempValue = parseInt(userText) + 0.01;
                    local_tempValue = _tempValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                }

                triggerNextStep({
                    currentStep,
                    value: userText,
                    localeText: local_tempValue,
                    currency: currentStep?.input?.currency,
                    trigger,
                });
            }
        }
    };

    const isFlowDisabled = (nextStep: ConversationStep) => {
        return nextStep?.disable && nextStep?.disable(values);
    };

    const handleFlowDisabled = async (
        currentStep: ConversationStep,
        nextStep: ConversationStep,
        data: any
    ) => {
        while (nextStep?.disable && nextStep?.disable(values)) {
            nextStep = await handleStepTrigger(nextStep, nextStep.trigger, data)!;
        }

        await handleNextStep(
            {
                ...currentStep,
                trigger: getTriggeredStep(nextStep?.id, data?.value),
            },
            data
        );
    };

    const handleStepTrigger = async (currentStep: ConversationStep, trigger: any, data: any) => {
        const switchFlowTrigger = handleSwitchFlow(currentStep);
        const customTrigger = await handleCustomTrigger(currentStep, data?.value!);

        if (switchFlowTrigger) {
            return switchFlowTrigger as ConversationStep;
        } else if (customTrigger) {
            return customTrigger as ConversationStep;
        }
        return getTriggeredStep(trigger, data?.value)!;
    };

    const getTriggeredStep = (trigger: any, value: any, flowSteps: ConversationStep[] = steps) => {
        return _.find(
            flowSteps,
            (s) =>
                s.id ===
                (typeof trigger === "function" ? trigger(values, value, dispatch) : trigger)
        );
    };

    const getStepMessage = (step: ConversationStep, nextMessage: string, prevMessage: string) => {
        if (`${nextMessage}`?.match(/{previousValue}/g)) {
            return `${nextMessage}`.replace(
                /{previousValue}/g,
                step?.formatter ? step?.formatter(prevMessage) : prevMessage
            );
        }
        const matches = `${nextMessage}`?.match(/{(.*?)}/g) || [];
        let newValue = `${nextMessage}`;

        for (const match of matches) {
            const _property: any = match.slice(1).replace("}", "");
            newValue = newValue.replace(match, dot.pick(_property, values));
        }
        return newValue;
    };

    const handleComponentNextStep = async (currentStep: ConversationStep, value: any) => {
        if (value?.value) {
            setValues({
                ...values,
                [value.key]: value.value,
            });
        }

        dispatch(
            chatActions.sendToCMS({
                message: {
                    ...currentStep?.trigger,
                    theme,
                    user: {
                        firstName: values?.applicant1_firstName,
                        lastName: values?.applicant1_lastName,
                        email: values?.applicant1_email,
                    },
                    escalated: currentStep?.escalated || chatHasBeenEscalated,
                },
            })
        );
    };

    const handleOptionNextStep = async (currentStep: ConversationStep, value: any) => {
        const options =
            typeof currentStep.options === "function"
                ? await currentStep.options(values)
                : currentStep.options;
        const option = options!.find((o) => o.value === value?.value);

        if (option) {
            const nextStep = getTriggeredStep(option?.trigger, option?.value);
            const step = {
                id: uuid(),
                user: {
                    firstName: values?.applicant1_firstName,
                    lastName: values?.applicant1_lastName,
                    email: values?.applicant1_email,
                },
                escalated: currentStep?.escalated || chatHasBeenEscalated,
                type: "user",
                end: currentStep.end,
                message: option.label,
                disable: nextStep?.disable,
                trigger: nextStep?.id,
                theme,
            };

            if (currentStep?.custom_value) {
                //const customValue = await currentStep?.custom_value!(data?.value, values);
                value = await currentStep?.custom_value!(value?.value, values);
            }

            setValues({
                ...values,
                [value?.key]: option?.value,
            });
            //setCurrentStep(step);
            //setRenderedSteps([...renderedSteps, step]);

            dispatch(
                chatActions.sendToCMS({
                    message: step,
                })
            );
        } else {
            await handleTextNextStep(currentStep, value);
        }
    };

    const handleTextNextStep = async (currentStep: ConversationStep, value: any) => {
        const valueToCheck = value?.localeText ? value?.localeText : value?.value;
        const step = valueToCheck
            ? {
                  id: uuid(),
                  type: "user",
                  end: currentStep?.end,
                  message:
                      currentStep?.formatter &&
                      !(
                          currentStep.theme?.apiKey === "abde4f0e-b4de-4166-8c87-a0bdf1500bb6" &&
                          valueToCheck === "Book an Appointment"
                      )
                          ? currentStep?.formatter(valueToCheck as string)
                          : valueToCheck,
                  trigger: currentStep?.trigger?.id,
                  theme,
              }
            : {
                  theme,
                  ...currentStep?.trigger,
              };

        if (value?.value || currentStep?.custom_value) {
            const custom = currentStep?.custom_value
                ? await currentStep?.custom_value(value?.value, values)
                : value;

            setValues({
                ...values,
                [custom.key]: custom.value,
            });
        }

        dispatch(
            chatActions.sendToCMS({
                message: {
                    ...step,
                    user: {
                        firstName: values?.applicant1_firstName,
                        lastName: values?.applicant1_lastName,
                        email: values?.applicant1_email,
                    },
                    escalated: currentStep?.escalated || chatHasBeenEscalated,
                },
            })
        );
    };

    const handleNextStep = async (currentStep: ConversationStep, value: any) => {
        if (currentStep?.component) {
            await handleComponentNextStep(currentStep, value);
        } else if (currentStep?.options?.length) {
            await handleOptionNextStep(currentStep, value);
        } else {
            await handleTextNextStep(currentStep, value);
        }
    };

    const triggerNextStep = async (
        data: {
            currentStep?: ConversationStep;
            value?: string | number;
            trigger?: string;
            condition?: string;
            localeText?: string;
            currency?: string;
        } = {}
    ) => {
        const { currentStep, value, trigger, localeText } = data;
        let step = Object.assign({}, currentStep);

        if (
            currentStep?.theme?.apiKey === "14b659e9-b757-49ee-9756-6cd1be0a0763" &&
            trigger === "fitchandfitch_calendly"
        ) {
            window.open(currentStep?.theme?.links?.calendarURL, "_blank");
        }

        if (await checkInvalidInput(step!, value)) {
            handleInputSubmit(step!, value!.toString(), trigger!);
        } else {
            const slicedLocaleText =
                localeText && localeText?.substring(0, localeText?.indexOf(".01"));

            let value = {
                ...values,
                key: step?.id!,
                value: data?.value,
                currency: data?.currency,
                localeText: slicedLocaleText,
            };

            step!.trigger = await handleStepTrigger(step!, trigger!, data);

            if (step?.trigger?.id && step?.trigger?.message) {
                const lastUserMessage = _.findLast(messages, (message) => message.type === "user");
                step.trigger = {
                    ...step.trigger,
                    message: getStepMessage(
                        step?.trigger,
                        typeof step?.trigger?.message === "function"
                            ? (step?.trigger?.message as any)(value)
                            : step?.trigger?.message,
                        lastUserMessage?.message!
                    ),
                };
            }

            if (isFlowDisabled(step?.trigger)) {
                await handleFlowDisabled(step, step?.trigger, data);
            } else {
                await handleNextStep(step!, value);
            }
        }
    };

    const hideAvatar = (step: ConversationStep) => {
        const stepIndex = _.lastIndexOf(messages, step);
        const nextStepIndex = Math.min(stepIndex + 1, messages.length - 1);
        const isLastElement = stepIndex === messages.length - 1;

        //show avatar if last element && !user
        if (isLastElement) {
            return false;
        }

        //if not last elemenet, but next rendered step is `user`
        if (!isLastElement && messages[nextStepIndex]?.type === "user") {
            return false;
        }

        return true;
    };

    const renderStep = (step: ConversationStep) => {
        if (step?.video) {
            return (
                <ChatMessageVideo
                    step={step}
                    values={values}
                    disableTypingAnimation={true}
                    disableScrollToBottom={step?.disableScrollToBottom}
                    triggerNextStep={triggerNextStep}
                    scrollToBottom={scrollToBottom}
                    videoLink={step?.video}
                    handleInputSubmit={handleInputSubmit}
                    setDataValues={setDataValues}
                />
            );
        }

        if (step?.input && step?.input?.type === "typeahead") {
            return (
                <ChatMesageInputTypeahead
                    step={step}
                    values={values}
                    disableScrollToBottom={step?.disableScrollToBottom}
                    triggerNextStep={triggerNextStep}
                    scrollToBottom={scrollToBottom}
                />
            );
        }

        if (step?.component) {
            return (
                //currentStep
                <ChatMessageCustom
                    setDataValues={setDataValues}
                    disableScrollToBottom={step?.disableScrollToBottom}
                    hideAvatar={hideAvatar(step) || step?.hideAvatar}
                    triggerNextStep={triggerNextStep}
                    values={values}
                    step={step}
                    scrollToBottom={scrollToBottom}
                />
            );
        }

        if (step?.options?.length) {
            return (
                <ChatMessageButton
                    hideAvatar={hideAvatar(step) || step?.hideAvatar}
                    triggerNextStep={triggerNextStep}
                    values={values}
                    step={step}
                    disableScrollToBottom={step?.disableScrollToBottom}
                    handleInputSubmit={handleInputSubmit}
                    setDataValues={setDataValues}
                    scrollToBottom={scrollToBottom}
                />
            );
        }

        if (step?.input && step?.input?.type === "textarea-solo") {
            return (
                <ChatMessageInput
                    hideAvatar={hideAvatar(step) || step?.hideAvatar}
                    handleInputSubmit={handleInputSubmit}
                    triggerNextStep={triggerNextStep}
                    values={values}
                    disableScrollToBottom={step?.disableScrollToBottom}
                    scrollToBottom={scrollToBottom}
                    step={step}
                    setDataValues={setDataValues}
                />
            );
        }

        if (step?.status) {
            return (
                <ChatMessageStatus
                    hideAvatar={hideAvatar(step) || step?.hideAvatar}
                    handleInputSubmit={handleInputSubmit}
                    triggerNextStep={triggerNextStep}
                    values={values}
                    disableScrollToBottom={step?.disableScrollToBottom}
                    scrollToBottom={scrollToBottom}
                    step={step}
                    setDataValues={setDataValues}
                />
            );
        }

        if (step?.message) {
            if (
                step?.type === "bot" &&
                step?.input &&
                (theme?.apiKey === "abde4f0e-b4de-4166-8c87-a0bdf1500bb6" ||
                    theme?.apiKey === "0155d30f-961b-42b8-9b07-7386aeb48913") &&
                !(
                    step?.id === "applicant1_firstName" ||
                    step?.id === "applicant1_lastName" ||
                    step?.id === "applicant1_email" ||
                    step?.id === "applicant1_contactNumber" ||
                    step?.id === "appointment_date" ||
                    step?.id === "select_date" ||
                    step?.id === "appointment_api" ||
                    step?.id === "loading_insurance" ||
                    step?.id === "loading_illustration" ||
                    step?.id === "illustration_page" ||
                    step?.id === "contact_requested"
                )
            ) {
                return (
                    <ChatMessageButton
                        hideAvatar={hideAvatar(step) || step?.hideAvatar}
                        triggerNextStep={triggerNextStep}
                        values={values}
                        step={step}
                        disableScrollToBottom={step?.disableScrollToBottom}
                        handleInputSubmit={handleInputSubmit}
                        setDataValues={setDataValues}
                        scrollToBottom={scrollToBottom}
                    />
                );
            } else {
                return (
                    <ChatMessageText
                        hideAvatar={hideAvatar(step) || step?.hideAvatar}
                        handleInputSubmit={handleInputSubmit}
                        triggerNextStep={triggerNextStep}
                        values={values}
                        disableScrollToBottom={step?.disableScrollToBottom}
                        scrollToBottom={scrollToBottom}
                        step={step}
                        setDataValues={setDataValues}
                    />
                );
            }
        }

        return null;
    };

    useEffect(() => {
        console.log("conversation add message");
        dispatch(
            chatActions.addMessage({
                message: {
                    ...steps[0],
                    theme,
                },
            })
        );
        /*eslint-disable react-hooks/exhaustive-deps*/
    }, []);

    return (
        <div
            className={`conversation-wrapper ${
                theme?.appConfig?.hideAppBar === "yes" ? "max-height" : ""
            }`}
            ref={bodyRef}
        >
            <div className="conversation-body">
                {React.Children.toArray(messages.map(renderStep)).filter(Boolean)}
            </div>
            {playAnimation && messages[messages.length - 1]?.playAnimation && (
                <div className="animation-wrapper">
                    <Lottie
                        animationData={animationData}
                        loop={false}
                        autoplay={true}
                        style={{ width: "100%", height: "100%" }}
                        onComplete={() => setPlayAnimation(false)}
                    />
                </div>
            )}
        </div>
    );
};

export default React.memo(Conversation);
